import React from 'react';
import { bool, func, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { AccordionCategory, Form, Button } from '../../components';
import { HR_STRATEGIC_SKILL_CATEGORIES } from '../../marketplace-custom-config';

import css from './EditListingHRStrategicSkillsForm.module.css';

const EditListingHRStrategicSkillsFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        handleSubmit,
        saveActionMsg,
        updated,
        pristine,
        updateInProgress,
        fetchErrors,
        values,
        form,
      } = formRenderProps;

      const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};

      const errorMessageUpdateListing = updateListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingHRStrategicSkillsForm.updateFailed" />
        </p>
      ) : null;

      const errorMessageCreateListingDraft = createListingDraftError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingHRStrategicSkillsForm.createListingDraftError" />
        </p>
      ) : null;

      const errorMessageShowListing = showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingHRStrategicSkillsForm.showListingFailed" />
        </p>
      ) : null;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const submitDisabled = disabled || submitInProgress;

      // hrStrategicSkills is stored as { [skillKey]: rating | null }
      const skillsObject = values.hrStrategicSkills || {};
      const selectedSkillKeys = Object.keys(skillsObject);

      const handleToggleSkill = skillKey => {
        const current = values.hrStrategicSkills || {};
        if (Object.prototype.hasOwnProperty.call(current, skillKey)) {
          // Uncheck: remove key
          const { [skillKey]: _removed, ...rest } = current;
          form.change('hrStrategicSkills', rest);
        } else {
          // Check: add key with null rating
          form.change('hrStrategicSkills', { ...current, [skillKey]: null });
        }
      };

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {errorMessageCreateListingDraft}
          {errorMessageUpdateListing}
          {errorMessageShowListing}

          <p className={css.description}>
            <FormattedMessage id="EditListingHRStrategicSkillsForm.description" />
          </p>

          <div className={css.accordionContainer}>
            {HR_STRATEGIC_SKILL_CATEGORIES.map(category => (
              <AccordionCategory
                key={category.key}
                category={category}
                selectedKeys={selectedSkillKeys}
                onToggleKey={handleToggleSkill}
                accordionItemClassName={css.accordionItem}
                accordionHeaderClassName={css.accordionHeader}
                accordionHeaderOpenClassName={css.accordionHeaderOpen}
                accordionLabelClassName={css.accordionLabel}
                selectedBadgeClassName={css.selectedBadge}
                accordionChevronClassName={css.accordionChevron}
                accordionBodyClassName={css.accordionBody}
                skillLabelClassName={css.skillLabel}
                skillCheckboxClassName={css.skillCheckbox}
              />
            ))}
          </div>

          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
        </Form>
      );
    }}
  />
);

EditListingHRStrategicSkillsFormComponent.defaultProps = {
  className: null,
  fetchErrors: null,
};

EditListingHRStrategicSkillsFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: propTypes.error,
};

export default compose(injectIntl)(EditListingHRStrategicSkillsFormComponent);
